import { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, onSnapshot, doc, updateDoc, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Notification } from '@/types';

export function useNotifications(userId: string, maxCount: number = 10) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    // グローバル通知を取得（管理者が投稿した全ユーザー向け通知）
    const globalNotificationsRef = collection(db, 'notifications');
    const globalQuery = query(
      globalNotificationsRef,
      where('targetUsers', '==', 'all'),
      orderBy('createdAt', 'desc'),
      limit(maxCount)
    );

    // ユーザー専用通知を取得
    const userNotificationsRef = collection(db, 'users', userId, 'notifications');
    const userQuery = query(userNotificationsRef, orderBy('createdAt', 'desc'), limit(maxCount));

    let globalNotifs: Notification[] = [];
    let userNotifs: Notification[] = [];
    let loadedCount = 0;

    const updateNotifications = () => {
      // 両方のリストをマージして、createdAtでソート
      // グローバル通知の read 状態を userId で判定
      const allNotifs = [...globalNotifs, ...userNotifs]
        .map((notif) => {
          if (notif.targetUsers) {
            // グローバル通知: readUsers に userId が含まれていれば既読
            const readUsers = notif.readUsers || [];
            return { ...notif, read: readUsers.includes(userId) };
          }
          // ユーザー専用通知: read フィールドをそのまま使用
          return notif;
        })
        .sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis())
        .slice(0, maxCount);

      setNotifications(allNotifs);
      setUnreadCount(allNotifs.filter((n) => !n.read).length);

      if (loadedCount === 2) {
        setLoading(false);
      }
    };

    // グローバル通知のリアルタイム購読
    const unsubscribeGlobal = onSnapshot(
      globalQuery,
      (snapshot) => {
        globalNotifs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Notification[];
        loadedCount++;
        updateNotifications();
      },
      (error) => {
        console.error('Error fetching global notifications:', error);
        globalNotifs = [];
        loadedCount++;
        updateNotifications();
      }
    );

    // ユーザー専用通知のリアルタイム購読
    const unsubscribeUser = onSnapshot(
      userQuery,
      (snapshot) => {
        userNotifs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Notification[];
        loadedCount++;
        updateNotifications();
      },
      (error) => {
        console.error('Error fetching user notifications:', error);
        userNotifs = [];
        loadedCount++;
        updateNotifications();
      }
    );

    return () => {
      unsubscribeGlobal();
      unsubscribeUser();
    };
  }, [userId, maxCount]);

  const markAsRead = async (notificationId: string) => {
    if (!userId) return;

    try {
      // 通知がグローバル通知かユーザー専用通知かを判定
      const notification = notifications.find((n) => n.id === notificationId);
      if (!notification) return;

      // グローバル通知の場合は users/{userId}/notificationReads に既読情報を保存
      // ユーザー専用通知の場合は read フィールドを更新
      if (notification.targetUsers) {
        // グローバル通知 → readUsers 配列に userId を追加
        const notifRef = doc(db, 'notifications', notificationId);
        const readUsers = notification.readUsers || [];
        if (!readUsers.includes(userId)) {
          await updateDoc(notifRef, { readUsers: [...readUsers, userId] });
        }
      } else {
        // ユーザー専用通知 → read フィールドを true に
        const notifRef = doc(db, 'users', userId, 'notifications', notificationId);
        await updateDoc(notifRef, { read: true });
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!userId) return;

    try {
      const unreadNotifications = notifications.filter((n) => !n.read);
      const updates = unreadNotifications.map((n) => {
        if (n.targetUsers) {
          // グローバル通知
          const notifRef = doc(db, 'notifications', n.id);
          const readUsers = n.readUsers || [];
          if (!readUsers.includes(userId)) {
            return updateDoc(notifRef, { readUsers: [...readUsers, userId] });
          }
        } else {
          // ユーザー専用通知
          const notifRef = doc(db, 'users', userId, 'notifications', n.id);
          return updateDoc(notifRef, { read: true });
        }
        return Promise.resolve();
      });
      await Promise.all(updates);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
  };
}
